import { Theme } from '@monorepo/relayably/theme'
import { AppleLogo, ArrowRight, EnvelopeSimple, GoogleLogo, LinkedinLogo, Phone } from 'phosphor-react-native'
import { useState } from 'react'
import { ActivityIndicator, Pressable, Switch, Text, TextInput, View } from 'react-native'
import { signInWithAppleSession, signInWithGoogleSession, signInWithLinkedInRedirect, submitAuth } from '../../auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../store'
import { createPaneStyles } from './shared'

export const LoginPane = ({
  theme,
  onForgotPassword,
  onOtpRequest,
  onSwitchToSignUp,
}: {
  theme: Theme
  onForgotPassword: () => void
  onOtpRequest: (phone: string) => void
  onSwitchToSignUp: () => void
}) => {
  const styles = createPaneStyles(theme)
  const dispatch = useAppDispatch()
  const auth = useAppSelector(state => state.auth)
  const [method, setMethod] = useState<'email' | 'phone'>('email')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const isLoading = auth.status === 'loading'

  return (
    <View style={styles.pane}>
      <View style={styles.socialGrid}>
        <Pressable disabled={isLoading} onPress={() => void dispatch(signInWithGoogleSession())} style={[styles.socialButton, isLoading ? styles.buttonDisabled : undefined]}>
          <GoogleLogo color={theme.colors.text} size={18} weight='bold' />
          <Text style={styles.socialButtonText}>Google</Text>
        </Pressable>
        <Pressable disabled={isLoading} onPress={() => void dispatch(signInWithAppleSession())} style={[styles.socialButton, isLoading ? styles.buttonDisabled : undefined]}>
          <AppleLogo color={theme.colors.text} size={18} weight='fill' />
          <Text style={styles.socialButtonText}>Apple</Text>
        </Pressable>
        <Pressable disabled={isLoading} onPress={() => void dispatch(signInWithLinkedInRedirect())} style={[styles.socialButton, isLoading ? styles.buttonDisabled : undefined]}>
          <LinkedinLogo color={theme.dark ? '#70b5f9' : '#0a66c2'} size={18} weight='fill' />
          <Text style={styles.socialButtonText}>LinkedIn</Text>
        </Pressable>
      </View>

      <View style={styles.divider}>
        <View style={styles.dividerLine} />
        <Text style={styles.dividerText}>Or continue with</Text>
        <View style={styles.dividerLine} />
      </View>

      <View style={styles.tabs}>
        <Pressable disabled={isLoading} onPress={() => setMethod('email')} style={[styles.tab, method === 'email' ? styles.tabActive : undefined, isLoading ? styles.buttonDisabled : undefined]}>
          <EnvelopeSimple color={method === 'email' ? theme.colors.text : theme.colors.inactive} size={16} />
          <Text style={[styles.tabText, method === 'email' ? styles.tabTextActive : undefined]}>Email</Text>
        </Pressable>
        <Pressable disabled={isLoading} onPress={() => setMethod('phone')} style={[styles.tab, method === 'phone' ? styles.tabActive : undefined, isLoading ? styles.buttonDisabled : undefined]}>
          <Phone color={method === 'phone' ? theme.colors.text : theme.colors.inactive} size={16} />
          <Text style={[styles.tabText, method === 'phone' ? styles.tabTextActive : undefined]}>Phone</Text>
        </Pressable>
      </View>

      <View style={styles.form}>
        {method === 'email' ? (
          <>
            <View style={styles.fieldGroup}>
              <Text style={styles.label}>Email Address</Text>
              <TextInput autoCapitalize='none' editable={!isLoading} keyboardType='email-address' onChangeText={setEmail} style={styles.input} value={email} />
            </View>
            <View style={styles.fieldGroup}>
              <View style={{ alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between' }}>
                <Text style={styles.label}>Password</Text>
                <Text onPress={isLoading ? undefined : onForgotPassword} style={styles.helperLink}>
                  Forgot?
                </Text>
              </View>
              <TextInput editable={!isLoading} onChangeText={setPassword} secureTextEntry style={styles.input} value={password} />
            </View>
          </>
        ) : (
          <View style={styles.fieldGroup}>
            <Text style={styles.label}>Phone Number</Text>
            <View style={styles.row}>
              <View style={[styles.input, { minWidth: 78, paddingHorizontal: 12 }]}>
                <Text style={{ color: theme.colors.text, fontSize: 14, textAlign: 'center' }}>+61</Text>
              </View>
              <TextInput editable={!isLoading} keyboardType='phone-pad' onChangeText={setPhone} style={[styles.input, { flex: 1 }]} value={phone} />
            </View>
          </View>
        )}

        {auth.errorMessage ? <Text style={styles.errorText}>{auth.errorMessage}</Text> : null}

        <Pressable
          disabled={isLoading}
          onPress={() => {
            if (method === 'phone') {
              onOtpRequest(phone)
              return
            }

            void dispatch(
              submitAuth({
                mode: 'sign-in',
                method: 'email',
                email,
                password,
                fullName: '',
                phoneNumber: '',
                otp: '',
              })
            )
          }}
          style={[styles.submitButton, isLoading ? styles.submitButtonDisabled : undefined]}
        >
          {isLoading ? (
            <ActivityIndicator color={theme.colors.staticWhite} size='small' />
          ) : (
            <>
              <Text style={styles.submitButtonText}>{method === 'phone' ? 'Send Code' : 'Sign In'}</Text>
              <ArrowRight color={theme.colors.staticWhite} size={16} weight='bold' />
            </>
          )}
        </Pressable>
      </View>

      <Text style={styles.footerText}>
        Don&apos;t have an account?
        <Text onPress={isLoading ? undefined : onSwitchToSignUp} style={styles.actionLink}>
          {' '}
          Create one
        </Text>
      </Text>

      <View style={styles.rememberRow}>
        <Switch
          disabled={isLoading}
          onValueChange={setRememberMe}
          thumbColor={rememberMe ? theme.colors.primary : theme.colors.border}
          trackColor={{ false: theme.colors.border, true: theme.colors.primary }}
          value={rememberMe}
        />
        <Text style={styles.rememberText}>Keep me signed in for 30 days</Text>
      </View>
    </View>
  )
}
